import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Calculator, Zap, Droplets, Home, Calendar } from "lucide-react";

function RentaAnual() {
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [resumen, setResumen] = useState({
        anio: new Date().getFullYear(),
        total_luz: 0,
        total_agua: 0,
        total_alquiler: 0,
        total_general: 0,
        detalles: [],
    });

    const cargarResumen = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get("/api/gastos/resumen-renta-anual", {
                params: { anio },
            });

            setResumen(response.data);
        } catch (err) {
            console.error("Error al cargar resumen de renta anual:", err);
            setError(
                err.response?.data?.message ||
                    "No se pudo cargar el resumen anual."
            );
        } finally {
            setLoading(false);
        }
    }, [anio]);

    useEffect(() => {
        cargarResumen();
    }, [cargarResumen]);

    const formatearMoneda = (cantidad) => {
        return new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
        }).format(Number(cantidad || 0));
    };

    const formatearFecha = (fecha) => {
        if (!fecha) {
            return "-";
        }

        return new Date(fecha).toLocaleDateString("es-ES");
    };

    const tiposTexto = {
        mensualidad: "Alquiler",
        luz: "Luz",
        agua: "Agua",
    };

    const aniosDisponibles = [];
    for (let y = 2023; y <= 2035; y += 1) {
        aniosDisponibles.push(y);
    }

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="renta-header">
                <div>
                    <h1>Resumen Anual para Renta</h1>
                    <p>Sumatorio anual separado por Luz, Agua y Alquiler.</p>
                </div>

                <div className="renta-filtro-anio">
                    <label className="form-label" htmlFor="anio-renta">
                        <Calendar size={16} />
                        Año fiscal
                    </label>
                    <select
                        id="anio-renta"
                        className="form-select"
                        value={anio}
                        onChange={(e) => setAnio(parseInt(e.target.value, 10))}
                    >
                        {aniosDisponibles.map((item) => (
                            <option key={item} value={item}>
                                {item}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="renta-grid">
                <div className="card renta-card renta-card-luz">
                    <div className="renta-card-top">
                        <div className="renta-icon-wrap">
                            <Zap size={20} />
                        </div>
                        <span className="badge badge-warning">Luz</span>
                    </div>
                    <h3>Total Luz ({resumen.anio})</h3>
                    <p className="renta-importe">{formatearMoneda(resumen.total_luz)}</p>
                    <small>Calculado por fecha de emision de factura.</small>
                </div>

                <div className="card renta-card renta-card-agua">
                    <div className="renta-card-top">
                        <div className="renta-icon-wrap">
                            <Droplets size={20} />
                        </div>
                        <span className="badge badge-success">Agua</span>
                    </div>
                    <h3>Total Agua ({resumen.anio})</h3>
                    <p className="renta-importe">{formatearMoneda(resumen.total_agua)}</p>
                    <small>Calculado por fecha de emision de factura.</small>
                </div>

                <div className="card renta-card renta-card-alquiler">
                    <div className="renta-card-top">
                        <div className="renta-icon-wrap">
                            <Home size={20} />
                        </div>
                        <span className="badge badge-secondary">Alquiler</span>
                    </div>
                    <h3>Total Alquiler ({resumen.anio})</h3>
                    <p className="renta-importe">{formatearMoneda(resumen.total_alquiler)}</p>
                    <small>Calculado por fecha de cargo.</small>
                </div>
            </div>

            <div className="card renta-total-card">
                <div className="renta-total-left">
                    <Calculator size={22} />
                    <div>
                        <h3>Total general deducible ({resumen.anio})</h3>
                        <p>Suma de Luz + Agua + Alquiler.</p>
                    </div>
                </div>
                <div className="renta-total-value">
                    {formatearMoneda(resumen.total_general)}
                </div>
            </div>

            <div className="card renta-detalle-card">
                <h2>Detalle de gastos incluidos ({resumen.anio})</h2>
                <p className="detalle-subtitle">
                    Tabla para revisar concepto, fecha de cargo e importe usados en el
                    cálculo anual.
                </p>

                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Concepto</th>
                                <th>Fecha de cargo</th>
                                <th>Importe</th>
                                <th>Tipo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resumen.detalles?.length ? (
                                resumen.detalles.map((gasto) => (
                                    <tr key={gasto.id}>
                                        <td>{gasto.concepto}</td>
                                        <td>{formatearFecha(gasto.fecha_cargo_ref)}</td>
                                        <td>{formatearMoneda(gasto.importe_total)}</td>
                                        <td>
                                            <span className="badge badge-secondary">
                                                {tiposTexto[gasto.tipo_gasto] || gasto.tipo_gasto}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="empty-row">
                                        No hay gastos incluidos para este año.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style jsx>{`
                .renta-header {
                    display: flex;
                    justify-content: space-between;
                    gap: 20px;
                    align-items: flex-end;
                    margin-bottom: 20px;
                }

                .renta-header h1 {
                    margin-bottom: 8px;
                }

                .renta-header p {
                    color: #6b7280;
                }

                .renta-filtro-anio {
                    min-width: 180px;
                }

                .renta-filtro-anio .form-label {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }

                .renta-grid {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 16px;
                }

                .renta-card {
                    margin-bottom: 0;
                    border-top: 4px solid transparent;
                }

                .renta-card-luz {
                    border-top-color: #f59e0b;
                }

                .renta-card-agua {
                    border-top-color: #10b981;
                }

                .renta-card-alquiler {
                    border-top-color: #64748b;
                }

                .renta-card-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .renta-icon-wrap {
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    background: #f3f4f6;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }

                .renta-card h3 {
                    font-size: 15px;
                    color: #374151;
                    margin-bottom: 8px;
                }

                .renta-importe {
                    font-size: 28px;
                    font-weight: 700;
                    color: #111827;
                    margin-bottom: 6px;
                }

                .renta-card small {
                    color: #6b7280;
                }

                .renta-total-card {
                    margin-top: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                }

                .renta-total-left {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .renta-total-left p {
                    color: #6b7280;
                    margin-top: 4px;
                }

                .renta-total-value {
                    font-size: 32px;
                    font-weight: 800;
                    color: #1d4ed8;
                }

                .renta-detalle-card {
                    margin-top: 20px;
                }

                .renta-detalle-card h2 {
                    margin-bottom: 8px;
                }

                .detalle-subtitle {
                    color: #6b7280;
                    margin-bottom: 16px;
                }

                .table-container {
                    overflow-x: auto;
                }

                .empty-row {
                    text-align: center;
                    padding: 32px;
                    color: #6b7280;
                }

                @media (max-width: 900px) {
                    .renta-grid {
                        grid-template-columns: 1fr;
                    }

                    .renta-header {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .renta-filtro-anio {
                        width: 100%;
                    }

                    .renta-total-card {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                }
            `}</style>
        </div>
    );
}

export default RentaAnual;
